
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, TrophyIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

// API & Context
import { getCompetitions } from '../../api/competitionApi.js'; 
import { createPaymentOrder, verifyPayment } from '../../api/paymentApi.js';
import { checkEnrollment } from '../../api/userApi.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { useToast } from '../../contexts/ToastContext.jsx';

// Sub-Components
import CompetitionCard from '../../components/CompetitionCard.jsx';
import InternshipCard from '../../components/InternshipCard.jsx';

const Competitions = () => {
  const { user } = useAuth();
  const showToast = useToast();
  const navigate = useNavigate();
  
  const [data, setData] = useState([]);
  const [pastData, setPastData] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('competition'); 

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCompetitions();
      
      const now = new Date();

      // Helper to normalize and categorize by date
      // const processItems = (items) => (items || []).map(item => ({
      //   ...item,
      //   id: item.id || item.competition_id,
      //   normalizedType: (item.type || 'competition').toLowerCase().trim(),
      //   isExpired: new Date(item.end_date) < now
      // }));
     
      const processItems = (items) => (items || []).map(item => {
  const parsedDate = item.end_date ? new Date(item.end_date) : null;
  return {
    ...item,
    id: item.id || item.competition_id,
    normalizedType: (item.type || 'competition').toLowerCase().trim(),
    isExpired: parsedDate ? parsedDate < now : false
  };
});


      // We combine all incoming data and re-sort them based on current real-time date
      const allItems = [
        ...processItems(res.competitions),
        ...processItems(res.pastCompetitions)
      ];

      // Separate based on date logic
      const active = allItems.filter(item => !item.isExpired);
      const past = allItems.filter(item => item.isExpired);

      setData(active);
      setPastData(past);

    } catch (err) {
      console.error("Fetch Error:", err);
      showToast('Arena sync failed', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // const handleJoin = async (item) => {
  //   if (!user) {
  //     showToast("Please log in to join", "info");
  //     navigate("/login");
  //     return;
  //   }

  //   try {
  //     setProcessingId(item.id);
  //     const enrollment = await checkEnrollment(item.id);
      
  //     if (enrollment?.isRegistered) {
  //       showToast("Already enrolled!", "success");
  //       navigate(item.normalizedType === 'internship' ? "/dashboard/my-internships" : "/dashboard/my-competitions");
  //       return;
  //     }

  //     const order = await createPaymentOrder({
  //       competitionId: item.id,
  //       amount: item.entry_fee || item.fee || 0,
  //     });

  //     const options = {
  //       key: process.env.REACT_APP_RAZORPAY_KEY_ID,
  //       amount: order.amount,
  //       currency: "INR",
  //       name: "GenZ Arena",
  //       order_id: order.id,
  //       handler: async (res) => {
  //         try {
  //           await verifyPayment({ ...res, competitionId: item.id });
  //           showToast("Success! Welcome to the Arena.", "success");
  //           navigate(item.normalizedType === 'internship' ? "/dashboard/my-internships" : "/dashboard/my-competitions");
  //         } catch (e) { showToast("Verification failed", "error"); }
  //       },
  //       prefill: { name: user.name, email: user.email },
  //       theme: { color: item.normalizedType === 'internship' ? "#10b981" : "#3b82f6" },
  //     };
  //     new window.Razorpay(options).open();
  //   } catch (err) {
  //     // Gracefully handle the "Expired" error from backend if date passed while user was on page
  //     showToast(err.response?.data?.message || "Action failed", "error");
  //   } finally {
  //     setProcessingId(null);
  //   }
  // };

  const handleJoin = async (item) => {
  if (processingId === item.id) return; // Prevent double click

  if (!user) {
    showToast("Please log in to join", "info");
    navigate("/login");
    return;
  }

  try {
    setProcessingId(item.id);

    const enrollment = await checkEnrollment(item.id);

    if (enrollment?.isRegistered) {
      showToast("Already enrolled!", "success");
      navigate(
        item.normalizedType === 'internship'
          ? "/dashboard/my-internships"
          : "/dashboard/my-competitions"
      );
      return;
    }

    const order = await createPaymentOrder({
      competitionId: item.id,
      amount: item.entry_fee || item.fee || 0,
    });

    if (!window.Razorpay) {
      showToast("Payment service unavailable. Please refresh.", "error");
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: "INR",
      name: "GenZ Arena",
      order_id: order.id,
      handler: async (res) => {
        try {
          await verifyPayment({ ...res, competitionId: item.id });
          showToast("Success! Welcome to the Arena.", "success");
          navigate(
            item.normalizedType === 'internship'
              ? "/dashboard/my-internships"
              : "/dashboard/my-competitions"
          );
        } catch (e) {
          showToast("Verification failed", "error");
        }
      },
      prefill: {
        name: user?.name || "",
        email: user?.email || ""
      },
      theme: {
        color: item.normalizedType === 'internship'
          ? "#10b981"
          : "#3b82f6"
      },
    };

    const razor = new window.Razorpay(options);
    razor.open();

  } catch (err) {
    showToast(
      err?.response?.data?.message || "Action failed",
      "error"
    );
  } finally {
    setProcessingId(null);
  }
};

  // Filter Logic for UI Tabs
  const matchesCategory = (item) => 
    activeCategory === 'internship' 
      ? item.normalizedType === 'internship' 
      : item.normalizedType !== 'internship';

  const filteredActive = data.filter(item => {
    // const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSearch = (item.title || "")
  .toLowerCase()
  .includes(searchQuery.toLowerCase());
    return matchesSearch && matchesCategory(item);
  });

  const filteredPast = pastData.filter(matchesCategory);

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-12 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Navigation & Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-12">
          <div className="flex bg-[#0c0c0c] p-1.5 rounded-2xl border border-white/5">
            <TabButton 
              active={activeCategory === 'competition'} 
              onClick={() => setActiveCategory('competition')}
              icon={<TrophyIcon className="h-4 w-4" />}
              label="Competition"
              count={data.filter(d => d.normalizedType !== 'internship').length}
            />
            <TabButton 
              active={activeCategory === 'internship'} 
              onClick={() => setActiveCategory('internship')}
              icon={<AcademicCapIcon className="h-4 w-4" />}
              label="Internship"
              count={data.filter(d => d.normalizedType === 'internship').length}
            />
          </div>

          <div className="relative w-full max-w-xs">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input 
              type="text" 
              placeholder="FILTER SIGNALS..."
              className="w-full bg-[#0c0c0c] border border-white/5 rounded-2xl pl-12 pr-4 py-4 text-[9px] font-black tracking-widest outline-none focus:border-white/20 transition-all"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* CONTENT RENDERING */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => <div key={i} className="h-80 bg-white/5 rounded-[2.5rem] animate-pulse" />)}
          </div>
        ) : (
          <>
            {/* 1. ACTIVE SECTION */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {filteredActive.length > 0 ? (
                  filteredActive.map(item => (
                    item.normalizedType === 'internship' 
                      ? <InternshipCard key={item.id} data={item} onJoin={() => handleJoin(item)} loading={processingId === item.id} />
                      : <CompetitionCard key={item.id} data={item} onJoin={() => handleJoin(item)} loading={processingId === item.id} />
                  ))
                ) : (
                  <div className="col-span-full py-20 text-center border border-dashed border-white/5 rounded-[3rem]">
                    <p className="text-[10px] text-gray-600 font-black uppercase tracking-[0.3em]">
                      No active {activeCategory}s available right now
                    </p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* 2. PAST SECTION (Explicitly showing if data exists) */}
            {filteredPast.length > 0 && (
              <div className="mt-40 border-t border-white/5 pt-20">
                <div className="flex items-center gap-6 mb-16">
                  <h2 className="text-[10px] font-black tracking-[0.4em] text-gray-600 uppercase whitespace-nowrap">
                     COMPLETED
                  </h2>
                  <div className="h-px w-full bg-gradient-to-r from-white/10 to-transparent"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {filteredPast.map(item => (
                    <div key={item.id} className="relative  flex h-full">
                      {item.normalizedType === 'internship' 
                        ? <InternshipCard data={item} disabled={true} /> 
                        : <CompetitionCard data={item} disabled={true} />
                      }
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
      active ? 'bg-white text-black' : 'text-gray-500 hover:text-white'
    }`}
  >
    {icon} {label} <span className="opacity-40 ml-1">({count})</span>
  </button>
);

export default Competitions;